import { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { Play, Pause, RotateCcw, Scissors } from 'lucide-react'
import { BEHAVIORAL_CODES } from './studentLabelingConfig'
import { REPLAY_EVENTS } from '../utils/replayAnnotations'

const EVENT_MARKERS = {
  RUN_CODE: { emoji: '▶', label: 'Run Code', color: '#10b981' },
  SUBMIT: { emoji: '📤', label: 'Submitted', color: '#6366f1' },
  PASTE: { emoji: '📋', label: 'Pasted text', color: '#f59e0b' },
  COPY: { emoji: '📑', label: 'Copied from the chat', color: '#f59e0b' },
  AWAY: { emoji: '💤', label: 'Away from the page', color: '#ef4444' },
  GAP: { emoji: '⏭', label: 'No activity (skipped)', color: '#9ca3af' },
}

// REPLAY_MARKER kinds an adapter can add (see replayAnnotations.js).
const ADAPTER_MARKERS = { submit: 'SUBMIT', paste: 'PASTE', copy: 'COPY', away: 'AWAY', gap: 'GAP' }

const MIN_SEGMENT_MS = 500 // Minimum segment duration when dragging

export default function ReplayControls({
  currentTime,
  duration,
  isPlaying,
  playbackSpeed,
  onTogglePlay,
  onSeek,
  onReset,
  onCycleSpeed,
  taggedBlocks = [],
  showLabeling = false,
  activeRecording = null,
  events = [],
  startTime = 0,
  showEventMarkers = true,
  isAutoMode = false,
  autoSegments = [],
  onAssignBehavior = null,
  onAdjustBoundary = null,
  onSliceSegment = null,
  formatTime: externalFormatTime = null,
  viewOnly = false,
  splitMarker = null,
  // Event markers normally show only on the labeling timeline; true also shows them on the
  // plain progress bar (View Student Data's replay of pages with adapter markers).
  markersWithoutLabeling = false,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [dropdownSegment, setDropdownSegment] = useState(null)
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 })
  const [showThinkingSubmenu, setShowThinkingSubmenu] = useState(false)
  const [draggingBoundary, setDraggingBoundary] = useState(null) // { index }
  const dropdownRef = useRef(null)
  const timelineRef = useRef(null)

  const formatTime = externalFormatTime || ((ms) => {
    const totalSeconds = Math.floor(ms / 1000)
    const m = Math.floor(totalSeconds / 60)
    const s = totalSeconds % 60
    return `${m}:${s < 10 ? '0' : ''}${s}`
  })

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
        setShowThinkingSubmenu(false)
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [dropdownOpen])

  const glowRef = useRef(null)

  // Boundary drag handlers
  const handleBoundaryDragStart = useCallback((index, e) => {
    e.stopPropagation()
    e.preventDefault()
    setDraggingBoundary({ index })
    setDropdownOpen(false)
    // Show glow via DOM
    const el = document.getElementById(`boundary-glow-${index}`)
    if (el) el.style.display = 'block'
    glowRef.current = index
  }, [])

  // Keep refs for values used during drag to avoid effect re-running
  const autoSegmentsRef = useRef(autoSegments)
  autoSegmentsRef.current = autoSegments
  const onAdjustBoundaryRef = useRef(onAdjustBoundary)
  onAdjustBoundaryRef.current = onAdjustBoundary
  const onSeekRef = useRef(onSeek)
  onSeekRef.current = onSeek
  const durationRef = useRef(duration)
  durationRef.current = duration

  // Permanent mouseup listener - never gets torn down
  useEffect(() => {
    const handleMouseUp = () => {
      setDraggingBoundary(null)
      // Hide glow via DOM
      if (glowRef.current !== null) {
        const el = document.getElementById(`boundary-glow-${glowRef.current}`)
        if (el) el.style.display = 'none'
        glowRef.current = null
      }
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [])

  // Mousemove only while dragging
  useEffect(() => {
    if (!draggingBoundary) return

    const handleMouseMove = (e) => {
      if (!timelineRef.current || !onAdjustBoundaryRef.current) return

      const rect = timelineRef.current.getBoundingClientRect()
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
      const ratio = x / rect.width
      const newTime = Math.round(ratio * durationRef.current)

      const segments = autoSegmentsRef.current
      const leftSegment = segments[draggingBoundary.index]
      const rightSegment = segments[draggingBoundary.index + 1]

      if (!leftSegment || !rightSegment) return

      const minTime = leftSegment.startTime + MIN_SEGMENT_MS
      const maxTime = rightSegment.endTime - MIN_SEGMENT_MS

      const clampedTime = Math.max(minTime, Math.min(newTime, maxTime))

      onAdjustBoundaryRef.current(draggingBoundary.index, clampedTime)
      onSeekRef.current(clampedTime)
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [draggingBoundary])

  const eventMarkers = useMemo(() => {
    if (!events || events.length === 0 || !showEventMarkers) return []

    const markers = []

    events.forEach((event) => {
      const eventTime = event.timestamp - startTime
      if (eventTime < 0 || eventTime > duration) return

      let markerType = null

      if (event.type === 'RUN_CODE' || event.type === 'RUN_TESTS' || event.type === 'TERMINAL_RUN') {
        markerType = 'RUN_CODE'
      } else if (event.type === REPLAY_EVENTS.MARKER) {
        markerType = ADAPTER_MARKERS[event.payload?.kind] || null
      }

      if (markerType) {
        markers.push({
          time: eventTime,
          type: markerType,
          config: EVENT_MARKERS[markerType],
          payload: event.payload,
        })
      }
    })

    return markers
  }, [events, startTime, duration, showEventMarkers])

  const renderEventMarker = (marker, index) => {
    const position = duration > 0 ? (marker.time / duration) * 100 : 0
    let transform = 'translate(-50%, -50%)'
    let hoverTransform = 'translate(-50%, -50%) scale(1.3)'
    if (position > 95) {
      transform = 'translate(-90%, -50%)'
      hoverTransform = 'translate(-90%, -50%) scale(1.3)'
    } else if (position < 5) {
      transform = 'translate(-10%, -50%)'
      hoverTransform = 'translate(-10%, -50%) scale(1.3)'
    }
    return (
      <div
        key={`${marker.type}-${index}`}
        title={`${marker.payload?.label || marker.config.label} at ${formatTime(marker.time)}`}
        style={{
          position: 'absolute',
          left: `${position}%`,
          top: '50%',
          transform: transform,
          fontSize: '14px',
          cursor: 'pointer',
          zIndex: 15,
          lineHeight: 1,
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
          transition: 'transform 0.1s',
        }}
        onClick={(e) => {
          e.stopPropagation()
          onSeek(marker.time)
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = hoverTransform
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = transform
        }}
      >
        {marker.config.emoji}
      </div>
    )
  }

  const handleSeek = (e) => {
    if (isAutoMode && !viewOnly) return 
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percentage = x / rect.width
    const newTime = Math.round(percentage * duration)
    onSeek(newTime)
  }

  const handleSegmentSeek = (e, segment) => {
    e.stopPropagation()
    if (draggingBoundary) return

    // In viewOnly mode, seek to click position (not segment start)
    if (viewOnly && timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const percentage = x / rect.width
      onSeek(Math.round(percentage * duration))
      return
    }

    onSeek(segment.startTime)
  }

  // Double click: open label dropdown
  const handleSegmentLabel = (e, segment) => {
    e.stopPropagation()
    if (viewOnly) return
    if (draggingBoundary) return

    const rect = e.currentTarget.getBoundingClientRect()
    const timelineRect = timelineRef.current?.getBoundingClientRect()
    
    setDropdownPosition({
      x: rect.left + rect.width / 2,
      y: timelineRect ? timelineRect.top - 8 : rect.top - 8
    })
    setDropdownSegment(segment)
    setDropdownOpen(true)
    setShowThinkingSubmenu(false)
  }

  const handleBehaviorSelect = (behavior, thinkingSubcategory = null) => {
    if (onAssignBehavior && dropdownSegment) {
      onAssignBehavior(dropdownSegment.id, behavior, thinkingSubcategory)
    }
    setDropdownOpen(false)
    setShowThinkingSubmenu(false)
  }

  // Check if playhead is inside a segment (for slice button state)
  const canSlice = isAutoMode && onSliceSegment && autoSegments.some(
    s => currentTime > s.startTime + MIN_SEGMENT_MS && currentTime < s.endTime - MIN_SEGMENT_MS
  )

  // Unlabeled segment color
  const UNLABELED_COLOR = '#6b7280'

  return (
    <div style={{
      flexShrink: 0,
      width: '100%',
      boxSizing: 'border-box',
      backgroundColor: '#1f2937',
      borderTop: '1px solid #374151',
      display: 'flex',
      flexDirection: 'column',
      padding: showLabeling ? '12px 24px 16px 24px' : '0 24px',
      color: '#f3f4f6',
      gap: showLabeling ? '10px' : '8px',
      boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.3)',
      position: 'relative',
    }}>

      {/* Dropdown Menu - hidden in viewOnly mode */}
      {dropdownOpen && dropdownSegment && !viewOnly && (
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            left: dropdownPosition.x,
            top: dropdownPosition.y,
            transform: 'translate(-50%, -100%)',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            zIndex: 1000,
            minWidth: '180px',
            overflow: 'hidden',
          }}
        >
          {/* Dropdown Header */}
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
          }}>
            <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>
              Label Segment
            </div>
            <div style={{ fontSize: '11px', color: '#374151' }}>
              {formatTime(dropdownSegment.startTime)} - {formatTime(dropdownSegment.endTime)}
            </div>
          </div>

          {/* Behavior Options */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {BEHAVIORAL_CODES.map((code) => {
              const isSelected = dropdownSegment.behavior?.id === code.id
              const hasSubcategories = code.subcategories && code.subcategories.length > 0
              
              return (
                <div key={code.id}>
                  <div
                    onClick={() => {
                      if (hasSubcategories) {
                        setShowThinkingSubmenu(showThinkingSubmenu ? false : true)
                      } else {
                        handleBehaviorSelect(code)
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? `${code.color}15` : 'transparent',
                      borderLeft: isSelected ? `3px solid ${code.color}` : '3px solid transparent',
                      transition: 'all 0.1s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '3px',
                      backgroundColor: code.color,
                    }} />
                    <span style={{ 
                      flex: 1,
                      fontSize: '12px', 
                      color: '#374151',
                      fontWeight: isSelected ? 600 : 400 
                    }}>
                      {code.label}
                    </span>
                    {hasSubcategories && (
                      <span style={{ fontSize: '10px', color: '#9ca3af' }}>▶</span>
                    )}
                    {isSelected && !hasSubcategories && (
                      <span style={{ fontSize: '12px', color: code.color }}>✓</span>
                    )}
                  </div>
                  
                  {/* Thinking Subcategories */}
                  {hasSubcategories && showThinkingSubmenu && (
                    <div style={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                      {code.subcategories.map(sub => {
                        const isSubSelected = dropdownSegment.thinkingSubcategory === sub.id
                        return (
                          <div
                            key={sub.id}
                            onClick={() => handleBehaviorSelect(code, sub.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '6px 12px 6px 30px',
                              cursor: 'pointer',
                              backgroundColor: isSubSelected ? `${code.color}10` : 'transparent',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubSelected) e.currentTarget.style.backgroundColor = '#f3f4f6'
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubSelected) e.currentTarget.style.backgroundColor = isSubSelected ? `${code.color}10` : 'transparent'
                            }}
                          >
                            <span style={{ 
                              fontSize: '11px', 
                              color: '#6b7280',
                              fontWeight: isSubSelected ? 600 : 400 
                            }}>
                              {sub.label}
                            </span>
                            {isSubSelected && (
                              <span style={{ fontSize: '11px', color: code.color }}>✓</span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Timeline Label Row */}
      {showLabeling && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 500 }}>
            Behavioral Timeline
          </span>
          <span style={{ color: '#9ca3af', fontSize: '11px' }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
      )}

      {/* Controls + Timeline Row */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px',
        flex: 1
      }}>
        {/* Controls Group */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={onReset} style={btnStyle} title="Reset">
            <RotateCcw size={20} />
          </button>

          <button onClick={onTogglePlay} style={btnStyle} title={isPlaying ? "Pause" : "Play"}>
            {isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" />}
          </button>

          {isAutoMode && onSliceSegment && (
            <button 
              onClick={onSliceSegment} 
              style={{ 
                ...btnStyle, 
                opacity: canSlice ? 1 : 0.3,
                cursor: canSlice ? 'pointer' : 'default',
              }} 
              title="Split segment at playhead"
              disabled={!canSlice}
            >
              <Scissors size={20} />
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div 
          ref={timelineRef}
          onClick={handleSeek}
          style={{ 
            flex: 1, 
            position: 'relative', 
            height: showLabeling ? '32px' : '8px', 
            backgroundColor: '#374151', 
            borderRadius: '4px',
            overflow: 'visible',
            cursor: (isAutoMode && !viewOnly) ? 'default' : 'pointer',
            boxSizing: 'border-box',
            userSelect: 'none'
          }}
        >
          {showLabeling ? (
            <>
              {isAutoMode ? (
                /* Auto-Segment Mode */
                <>
                  {autoSegments.map((segment, index) => {
                    const startPercent = duration > 0 ? (segment.startTime / duration) * 100 : 0
                    const endPercent = duration > 0 ? (segment.endTime / duration) * 100 : 0
                    const width = endPercent - startPercent
                    const isLabeled = segment.behavior !== null
                    const isSelected = dropdownSegment?.id === segment.id && dropdownOpen
                    const displayColor = isLabeled ? segment.behavior.color : UNLABELED_COLOR
                    
                    return (
                      <div
                        key={segment.id}
                        onClick={(e) => handleSegmentSeek(e, segment)}
                        onDoubleClick={(e) => handleSegmentLabel(e, segment)}
                        title={`${isLabeled ? segment.behavior.label : 'Double-click to label'} (${formatTime(segment.startTime)} - ${formatTime(segment.endTime)})`}
                        style={{
                          position: 'absolute',
                          left: `${startPercent}%`,
                          width: `${Math.max(width, 0.5)}%`,
                          height: '100%',
                          backgroundColor: displayColor,
                          opacity: isLabeled ? 0.85 : 0.4,
                          border: isSelected 
                            ? '2px solid #fbbf24' 
                            : `1px solid ${isLabeled ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'}`,
                          borderRadius: '2px',
                          boxSizing: 'border-box',
                          cursor: draggingBoundary ? 'col-resize' : 'pointer',
                          zIndex: isSelected ? 10 : 5,
                          transition: draggingBoundary ? 'none' : 'all 0.15s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected && !draggingBoundary) {
                            e.currentTarget.style.opacity = isLabeled ? '1' : '0.6'
                            e.currentTarget.style.transform = 'scaleY(1.1)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected && !draggingBoundary) {
                            e.currentTarget.style.opacity = isLabeled ? '0.85' : '0.4'
                            e.currentTarget.style.transform = 'scaleY(1)'
                          }
                        }}
                      >
                        {/* Segment number */}
                        {width > 3 && (
                          <span style={{
                            fontSize: '9px',
                            fontWeight: 700,
                            color: isLabeled ? '#fff' : 'rgba(255,255,255,0.6)',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                          }}>
                            {index + 1}
                          </span>
                        )}
                      </div>
                    )
                  })}

                  {/* Draggable boundary handles between segments */}
                  {!viewOnly && autoSegments.map((segment, index) => {
                    if (index >= autoSegments.length - 1) return null
                    const boundaryTime = segment.endTime
                    const boundaryPercent = duration > 0 ? (boundaryTime / duration) * 100 : 0

                    return (
                      <div
                        key={`boundary-${index}`}
                        onMouseDown={(e) => handleBoundaryDragStart(index, e)}
                        title={`Drag to adjust · ${formatTime(boundaryTime)}`}
                        style={{
                          position: 'absolute',
                          left: `${boundaryPercent}%`,
                          top: 0,
                          bottom: 0,
                          width: '10px',
                          transform: 'translateX(-50%)',
                          cursor: 'col-resize',
                          zIndex: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          id={`boundary-glow-${index}`}
                          style={{
                            display: 'none',
                            width: '3px',
                            height: '100%',
                            backgroundColor: '#fbbf24',
                            borderRadius: '1px',
                            boxShadow: '0 0 6px rgba(251,191,36,0.6)',
                          }}
                        />
                      </div>
                    )
                  })}
                </>
              ) : (
                /* Manual Mode */
                <>
                  {taggedBlocks.map((block) => {
                    const startPercent = duration > 0 ? (block.startTime / duration) * 100 : 0
                    const endPercent = duration > 0 ? (block.endTime / duration) * 100 : 0
                    const width = endPercent - startPercent
                    return (
                      <div
                        key={block.id}
                        title={`${block.behavior?.label || 'Behavior'}: ${formatTime(block.startTime)} - ${formatTime(block.endTime)}`}
                        style={{
                          position: 'absolute',
                          left: `${startPercent}%`,
                          width: `${Math.max(width, 0.5)}%`,
                          height: '100%',
                          backgroundColor: block.behavior?.color || '#f59e0b',
                          opacity: 0.8,
                          zIndex: 5,
                        }}
                      />
                    )
                  })}

                  {activeRecording && (
                    <div
                      title={`Recording: ${activeRecording.behavior?.label || 'Recording'}`}
                      style={{
                        position: 'absolute',
                        left: `${(activeRecording.startTime / duration) * 100}%`,
                        width: `${((currentTime - activeRecording.startTime) / duration) * 100}%`,
                        height: '100%',
                        backgroundColor: activeRecording.behavior?.color || '#ef4444',
                        opacity: 0.6,
                        animation: 'pulse 1s infinite',
                        zIndex: 6,
                      }}
                    />
                  )}
                </>
              )}

              {/* Event Markers */}
              {eventMarkers.map(renderEventMarker)}
            </>
          ) : (
            <>
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: `${progress}%`,
                backgroundColor: '#3b82f6',
                borderRadius: '4px',
              }} />
              {markersWithoutLabeling && eventMarkers.map(renderEventMarker)}
            </>
          )}

          {/* Split point marker */}
          {splitMarker && duration > 0 && (
            <div style={{
              position: 'absolute',
              left: `${(splitMarker.time / duration) * 100}%`,
              top: 0,
              bottom: 0,
              width: '3px',
              backgroundColor: splitMarker.color || '#f59e0b',
              zIndex: 25,
              pointerEvents: 'none',
              borderRadius: '1px',
              transform: 'translateX(-50%)',
            }}>
              <div style={{
                position: 'absolute',
                top: '-5px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '9px',
                height: '9px',
                borderRadius: '50%',
                backgroundColor: splitMarker.color || '#f59e0b',
                border: '2px solid #1f2937',
              }} />
            </div>
          )}

          {/* Playhead */}
          <div style={{
            position: 'absolute',
            left: `${progress}%`,
            top: 0,
            bottom: 0,
            width: '3px',
            backgroundColor: '#ffffff',
            boxShadow: '0 0 6px rgba(255,255,255,0.8)',
            zIndex: 30,
            pointerEvents: 'none',
            transform: 'translateX(-50%)'
          }} />
        </div>

        {/* Speed Toggle */}
        <button 
          onClick={onCycleSpeed} 
          style={{ ...btnStyle, fontSize: '12px', minWidth: '40px', fontWeight: 'bold' }}
          title="Playback Speed"
        >
          {playbackSpeed}x
        </button>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  )
}

const btnStyle = {
  background: 'none',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px',
  borderRadius: '4px',
  transition: 'background 0.2s'
}